import React from 'react';
import { flow } from 'fp-ts/function';
import {
  getColumnEntities,
  getColumnName,
  getFullTableName,
  groupColumnsBy,
  getMatchingPredicateFor,
  groupBySchemaName,
} from './utils';
import { useSearchContext } from '../../context';
import { TableInfo } from './subcomponents/TableInfo';
import { TableEntity } from './types';
import styles from './SchemaEntitiesList.module.scss';

export type SchemaNavigationListProps = {
  tables: TableEntity[];
  visibleTablesNames?: string[];
  onAddTable?: F<TableEntity, void>;
};

export const SchemaEntitiesList: React.FC<SchemaNavigationListProps> = ({
  tables,
  onAddTable,
  visibleTablesNames = [],
}) => {
  const [_, searchTerms] = useSearchContext();

  const groupedTables = groupBySchemaName(tables);
  const searchPredicate = getMatchingPredicateFor(searchTerms);
  const columnPredicate = flow(getColumnName, searchPredicate);

  return (
    <>
      {groupedTables.map(([schemaName, schemaTables], schemaGroupIndex) => {
        const tableContents = schemaTables.reduce(
          (content, table, tableIndex) => {
            const tableMatchesSearch = searchPredicate(table.table());
            const columns = getColumnEntities(table);
            const groupedColumns = groupColumnsBy(columnPredicate, columns);
            const someColumnsMatchSearch = groupedColumns.matching.length !== 0;

            return tableMatchesSearch || someColumnsMatchSearch
              ? [
                  ...content,
                  <TableInfo
                    key={tableIndex}
                    table={table}
                    onAddTable={onAddTable}
                    columns={groupedColumns}
                    onMap={visibleTablesNames.includes(getFullTableName(table))}
                  />,
                ]
              : content;
          },
          [] as JSX.Element[],
        );

        const showSchemaHeader = !(
          groupedTables.length === 1 && groupedTables[0][0] === 'public'
        );

        return (
          <div key={`schema_${schemaGroupIndex}`}>
            {showSchemaHeader && (
              <div className={styles.schemaNameContainer}>
                <span className={styles.schemaName}>{schemaName} schema</span>
              </div>
            )}
            {tableContents.length === 0 ? (
              <div className={styles.noTablesInfo}>
                no matching tables or columns
              </div>
            ) : (
              tableContents
            )}
          </div>
        );
      })}
    </>
  );
};

export { getFullTableName } from './utils';
